import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import { toast } from "react-toastify";
import { useRenameVisaTemplateField, useVisaTemplateFields } from "utils/visaDocumentService";

const FieldNameInput = ({ field, templateId, linked }) => {
  const [value, setValue] = useState(field.name);

  useEffect(() => {
    setValue(field.name);
  }, [field.name]);

  const { mutate: renameField, isLoading } = useRenameVisaTemplateField({
    onSuccess: (data) => {
      setValue(data.name);
      toast.success("Field renamed");
    },
    onError: (err) => {
      setValue(field.name);
      toast.error(err?.response?.data?.errorMessage || "Failed to rename field");
    },
  });

  const commit = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === field.name) {
      setValue(field.name);
      return;
    }
    renameField({ templateId, fieldId: field.id, name: trimmed });
  };

  return (
    <TextField
      size="small"
      value={value}
      disabled={isLoading}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      InputProps={{
        endAdornment: linked ? (
          <LinkIcon fontSize="small" color="success" sx={{ ml: 0.5 }} />
        ) : undefined,
      }}
      sx={{ minWidth: 220 }}
    />
  );
};

const VisaTemplateFieldsDialog = ({ open, onClose, templateId, templateTitle }) => {
  const { data: fields = [], isFetching } = useVisaTemplateFields(templateId, { enabled: open });

  const nameCounts = fields.reduce((acc, f) => {
    acc[f.name] = (acc[f.name] || 0) + 1;
    return acc;
  }, {});

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        <span className="font-semibold">Fields — {templateTitle}</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Rename a field to match another field's name (of the same type) to have them fill together
          everywhere — e.g. an initials box repeated on every page.
        </Alert>

        {isFetching ? (
          <Box className="flex justify-center py-4">
            <CircularProgress size={22} />
          </Box>
        ) : fields.length === 0 ? (
          <Alert severity="warning">No fillable fields detected in this PDF.</Alert>
        ) : (
          <Stack spacing={1}>
            {fields.map((field) => (
              <Stack
                key={field.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ py: 0.5 }}
              >
                <Chip
                  size="small"
                  label={`Page ${field.pageNumber}`}
                  sx={{ height: 22, fontSize: 11, flexShrink: 0 }}
                />
                <Chip
                  size="small"
                  label={field.fieldType}
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11, flexShrink: 0, width: 90 }}
                />
                <Box className="min-w-0 flex-1">
                  <Typography variant="caption" color="text.secondary" className="block truncate">
                    {field.label}
                  </Typography>
                  <FieldNameInput
                    field={field}
                    templateId={templateId}
                    linked={nameCounts[field.name] > 1}
                  />
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VisaTemplateFieldsDialog;
